import { signOut } from "@bindery/auth-consumer/browser";
import { readerRange, type ReaderDb } from "@/storage/db";
import { forgetDeviceReader } from "@/storage/device-readers";
import { clearUnconfirmed } from "@/storage/unconfirmed-reader";
import { forgetMilestones } from "./milestone";

export const RESET_RETURN_TO = "/signed-out";
export const RESET_STEP_TIMEOUT_MS = 5_000;

export type ResetOutcome = "reset" | "unsent";

export interface ResetProgressDeps {
  flushOutbox: () => Promise<void>;
  /** Anything of this reader's that could still send: queued or held. */
  hasUnsentRows: (readerId: string) => Promise<boolean>;
  forgetLocalReader: (readerId: string) => Promise<void>;
  forgetDeviceMarks: (readerId: string) => void;
  signOutTo: (returnTo: string) => void;
  timeoutMs?: number;
}

export async function hasUnsentRows(
  db: ReaderDb,
  readerId: string,
): Promise<boolean> {
  const [lower, upper] = readerRange(readerId);
  const counts = await db.transaction(
    "r",
    [db.outboxRecords, db.outboxResponses, db.outboxBookmarks],
    () =>
      Promise.all([
        db.outboxRecords
          .where("[readerId+queuedAt]")
          .between(lower, upper)
          .count(),
        db.outboxResponses
          .where("[readerId+queuedAt]")
          .between(lower, upper)
          .count(),
        db.outboxBookmarks
          .where("[readerId+readingId]")
          .between(lower, upper)
          .filter((row) => row.state === "pending")
          .count(),
      ]),
  );
  return counts.some((count) => count > 0);
}

const loadSync = () => import("@/sync");

export const browserResetDeps: ResetProgressDeps = {
  flushOutbox: () => loadSync().then((sync) => sync.outbox.flush()),
  hasUnsentRows: (readerId) =>
    loadSync().then((sync) => hasUnsentRows(sync.readerDb, readerId)),
  forgetLocalReader: (readerId) =>
    loadSync().then((sync) => sync.forgetLocalReader(readerId)),
  forgetDeviceMarks: (readerId) => {
    clearUnconfirmed(readerId);
    forgetDeviceReader(readerId);
    forgetMilestones(readerId);
  },
  signOutTo: signOut,
};

const TIMED_OUT = Symbol("timed out");

export async function within<T>(
  work: () => Promise<T>,
  ms: number,
): Promise<T | typeof TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), ms);
  });
  try {
    return await Promise.race([work(), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * ../app reset by signing out and wiping every client store. Here only the
 * current reader's local copy goes, as "Not me" does, and only once its
 * outbox holds nothing that could still send: those rows are research data
 * that flush only under this reader's session, and after a reset nothing
 * on this device would sign in as it again to send them. Quarantined rows
 * do not block: the server refused them, and forgetReader keeps them. A
 * check that fails or stalls counts as unsent.
 */
export async function resetReaderProgress(
  readerId: string,
  {
    flushOutbox,
    hasUnsentRows: unsent,
    forgetLocalReader,
    forgetDeviceMarks,
    signOutTo,
    timeoutMs = RESET_STEP_TIMEOUT_MS,
  }: ResetProgressDeps,
): Promise<ResetOutcome> {
  await within(() => flushOutbox().catch(() => undefined), timeoutMs);
  const pending = await within(
    () => unsent(readerId).catch(() => true),
    timeoutMs,
  );
  if (pending !== false) return "unsent";
  await within(
    () => forgetLocalReader(readerId).catch(() => undefined),
    timeoutMs,
  );
  forgetDeviceMarks(readerId);
  signOutTo(RESET_RETURN_TO);
  return "reset";
}
